<template>
  <section v-if="tenant.state" id="returningUser">
    <div class="h-screen bg-white">
      <div class="max-w-8xl mx-auto text-center py-8 px-4 sm:px-6 lg:px-6 lg:py-[180px]">
        <div class="space-y-5 sm:space-y-4 sm:mx-auto lg:max-w-8xl">
          <h1 class="mb-8 text-2xl font-bold text-green-400 sm:text-5xl">
            Willkommen zurück!
          </h1>
          <h2 class="mt-8 text-lg text-gray-600 font-bold">{{ stateDescription.headline }}</h2>
          <p class="mt-4 text-lg leading-6 text-gray-600">{{ stateDescription.description }}</p>
        </div>
        <div v-if="stateDescription.buttons && stateDescription.buttons.length > 0"
          class="sm:space-x-8 sm:mt-16 sm:flex sm:justify-center">
          <div v-for="(button, index) in stateDescription.buttons" :key="index" @click="button.click" class="my-8 cursor-pointer inline-block text-center text-lg font-medium border-2
            border-green-400 rounded-full text-green-400 px-12 py-3 hover:bg-green-400 hover:text-white">
            {{ button.text }}
          </div>
        </div>
        <div
          class="cursor-pointer flex items-center justify-center text-base text-center text-white lg:text-gray-600 font-light underline px-4 py-3 sm:px-8 w-full sm:w-auto">
          <a href="/app/start?id=new">Ich möchte nochmal von vorne anfangen</a>
        </div>
      </div>
    </div>
  </section>
  <section v-else id="reservationOrApplication">
    <div class="h-screen bg-white">
      <div class="max-w-8xl mx-auto text-center py-8 px-4 sm:px-6 lg:px-6 lg:py-[180px]">
        <div class="space-y-5 sm:space-y-4 sm:mx-auto lg:max-w-8xl">
          <h1 class="mb-8 text-2xl font-bold text-green-400 sm:text-5xl">
            Haben Sie schon eine Zusage für Ihre neue Wohnung?
          </h1>
          <p class="mt-4 text-lg leading-6 text-gray-600">
            Eine mündliche Bestätigung des Vermieters, dass Sie die Wohnung bekommen, ist bereits ausreichend.
          </p>
        </div>
        <div class="sm:space-x-8 sm:mt-16 sm:flex sm:justify-center">
          <router-link :to="redirectBuergschaft" @click="routeToStart('application');" class="w-full my-8 cursor-pointer block whitespace-nowrap text-center text-lg font-medium border-2
                        border-green-400 rounded-full text-green-400 px-12 py-3 hover:bg-green-400 hover:text-white sm:w-2/5
                          md:inline xl:w-1/5">
            Ja
          </router-link>

          <router-link :to="redirectReservierung" @click="routeToStart('reservation');" class="w-full my-8 cursor-pointer block whitespace-nowrap text-center text-lg font-medium border-2
                    border-green-400 rounded-full text-green-400 px-12 py-3 hover:bg-green-400 hover:text-white sm:w-2/5
                    md:inline xl:w-1/5">
            Nein
          </router-link>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { useTenantStore } from "../stores/tenant";
import { onMounted, ref, computed } from "vue";
import { useRoute } from 'vue-router';
import router from "../utils/router";

const route = useRoute();
defineProps({
  id: String,
})

const tenant = useTenantStore();
const redirectBuergschaft = ref('/app/start-buergschaft');
const redirectReservierung = ref('/app/start-reservierung');
const reservationContent = ref({
  headline: 'Sie haben bereits eine Reservierung abgeschlossen',
  description: 'Die Unterlagen zur Reservierung sollten in den kommenden Minuten in Ihrem E-Mail-Postfach sein. Alternativ können Sie diese auch in Ihrem Heysafe-Konto herunterladen.',
  buttons: [
    {
      text: 'Zum Konto',
      click: () => { window.location.pathname = '/konto'; },
    }
  ]
});


onMounted(async () => {
  const getParameterDeposit = route.query.deposit;
  if(getParameterDeposit) {
    tenant.setFormValue("next_depositAmount",  getParameterDeposit);
  }

  const getParameterId = route.query.id;

  if (getParameterId === 'new') {
    tenant.resetData();
    return;
  }
  await tenant.loadUserData();

  if (tenant.reservationOlderThan10Minutes) {
    reservationContent.value = reservationContentAfterTenMinutes;
  }
})

function routeToStart(target) {
  if (target === 'application') {
    tenant.setProcessingRequestedState('alteos_accepted');
    tenant.setIsReservation(false);
    router.push('/app/start-buergschaft')
  } else {
    tenant.setProcessingRequestedState('customer_reservation')
    tenant.setIsReservation(true);
    router.push('/app/start-reservierung')
  }
}

const reservationContentAfterTenMinutes = {
  headline: 'Sie haben bereits eine Reservierung abgeschlossen',
  description: 'Haben Sie eine Wohnung gefunden? Dann können Sie Ihre Reservierung in eine Bürgschaft umwandeln. Nach erfolgreichem Abschluss landet Ihre digitale Urkunde direkt in Ihrem heysafe Konto.',
  buttons: [
    {
      text: 'Reservierung in Bürgschaft umwandeln',
      click: tenant.navigate,
    }
  ]
};


const stateDescription = computed(() => {

  switch (tenant.state) {
    default:
    case 'submitted':
    case 'creditcheck_submit':
    case 'policy_id_pending':
    case 'error':
    case 'error_critical':
      return {
        headline: 'Ihre Anfrage wird bearbeitet',
        description: 'Sie sollten in den nächsten 30-45 Minuten eine E-Mail erhalten. Falls nicht, kontaktieren Sie uns bitte.',
      };
    case 'documents_pending':
      return {
        headline: 'Ihre Anfrage wird bearbeitet',
        description: 'Wir haben Ihre Daten an die AXA Versicherung weitergeleitet. Wir warten aktuell noch auf die Bürgschaftsurkunde. Sie sollten in den nächsten 30-45 Minuten eine E-Mail erhalten. Falls nicht, kontaktieren Sie uns bitte.',
      };
    case 'alteos_accepted':
      return {
        headline: 'Vervollständigen Sie Ihre Daten',
        description: 'Ein paar Daten fehlen noch, damit wir Ihren Vertrag abschließen können.',
        buttons: [
          {
            text: 'Weiter',
            click: tenant.navigate,
          }
        ]
      };
    case 'reservation_issued':
      return {
        headline: reservationContent.value.headline,
        description: reservationContent.value.description,
        buttons: reservationContent.value.buttons
      };

    case 'finished':
      return {
        headline: 'Sie haben bereits eine Kautionsbürgschaft abgeschlossen',
        description: 'Wir haben bereits alle notwendigen Schritte für Sie erledigt. Sie können sich entspannt zurücklehnen. Ihre Bürschaftsurkunde finden Sie in Ihrem Konto.',
        buttons: [
          {
            text: 'Zur Bürgschaft',
            click: () => { window.location.pathname = '/konto'; },
          },
          {
            text: 'Neuen Antrag stellen',
            click: tenant.resetData,
          }
        ]
      };
    case 'alteos_rejected':
      return {
        headline: 'Ihre Anfrage wurde leider von der AXA abgelehnt',
        description: 'Wir können Ihnen keine Mietkautionsbürgschaft anbieten.',
      }
  }
});

</script>
